import { useEffect, useRef, useState } from 'react';
import { Monitor, Smartphone, Tablet } from 'lucide-react';
import { SegmentedControl } from '@mlm/ui';
import type { RpLayout } from '@mlm/shared';
import type { RpPackageCard } from '@mlm/ui';
import { memberPreviewUrl, memberOrigin } from '../../lib/member-origin';

export type RpDevice = 'desktop' | 'tablet' | 'mobile';

/** Real device widths, so the frame trips the same breakpoints a phone does. */
const DEVICE_WIDTH: Record<RpDevice, string> = {
  desktop: '100%',
  tablet: '834px',
  mobile: '390px',
};

/** How long to wait for the frame's `ready` handshake before giving up on it. */
const HANDSHAKE_MS = 2500;

/**
 * Device-accurate preview: the member app's preview route in an iframe, fed the
 * draft over `postMessage`.
 *
 * The reason this exists rather than just rendering the draft in the admin:
 * Tailwind's responsive prefixes are VIEWPORT media queries. A narrow div inside
 * a wide admin window still renders desktop styles, so an in-page device toggle
 * would show something no visitor will ever see. An iframe carries its own
 * viewport, so narrowing it triggers the real mobile layout — which matters
 * because these links are opened overwhelmingly on phones.
 *
 * Degrades honestly: if the member origin can't be resolved or the frame never
 * answers, `onUnavailable` fires and the caller falls back to the in-page
 * preview rather than showing a broken box.
 */
export function RpPreviewFrame({
  layout,
  device,
  ctaLabel,
  ctaAuto,
  packages,
  selectedId,
  onSelect,
  onUnavailable,
}: {
  layout: RpLayout;
  device: RpDevice;
  ctaLabel: string;
  ctaAuto: boolean;
  packages: RpPackageCard[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onUnavailable: (reason: string) => void;
}) {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [ready, setReady] = useState(false);
  const url = memberPreviewUrl();
  const origin = memberOrigin();

  // Listen for the frame's handshake and its click-to-select messages.
  useEffect(() => {
    if (!origin) return;
    function onMessage(e: MessageEvent) {
      if (e.origin !== origin) return;
      const d = e.data as { __rp?: string; id?: string } | null;
      if (d?.__rp === 'ready') setReady(true);
      else if (d?.__rp === 'select' && typeof d.id === 'string') onSelect(d.id);
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [origin, onSelect]);

  // If the frame never answers, say so instead of leaving a blank rectangle.
  useEffect(() => {
    if (!url) {
      onUnavailable('no member URL configured');
      return;
    }
    if (ready) return;
    const t = setTimeout(() => {
      if (!ready) onUnavailable('the preview frame did not respond');
    }, HANDSHAKE_MS);
    return () => clearTimeout(t);
  }, [url, ready, onUnavailable]);

  // Push the draft whenever it changes. Debounced, because this fires on every
  // keystroke in the inspector and re-rendering a whole page per character is
  // wasted work.
  useEffect(() => {
    if (!ready || !origin) return;
    const t = setTimeout(() => {
      frameRef.current?.contentWindow?.postMessage(
        { __rp: 'draft', layout, ctaLabel, ctaAuto, packages, selectedId },
        // Explicit target origin — never '*', which would hand the draft to
        // whatever happened to be framed.
        origin,
      );
    }, 120);
    return () => clearTimeout(t);
  }, [ready, origin, layout, ctaLabel, ctaAuto, packages, selectedId]);

  if (!url) return null;

  return (
    <div className="flex justify-center overflow-x-auto rounded-md border bg-muted/40 p-3">
      <iframe
        ref={frameRef}
        src={url}
        title="Page preview"
        className="h-[720px] shrink-0 rounded-md border bg-background shadow-sm transition-[width] duration-200"
        style={{ width: DEVICE_WIDTH[device] }}
        // The frame renders admin-authored content on a different origin; keep
        // it from navigating this window or opening popups. Deliberately absent:
        // `allow-top-navigation` and `allow-popups` — so a target="_blank" CTA is
        // inert HERE but works live. That is the one accepted divergence.
        sandbox="allow-scripts allow-same-origin allow-forms allow-presentation"
        // Permissions Policy is DELEGATED down the frame tree: without this the
        // member document cannot pass autoplay/fullscreen/PiP/EME on to a Vimeo
        // or YouTube player, and the preview reports a video as broken that
        // plays fine on the live page.
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        referrerPolicy="strict-origin-when-cross-origin"
      />
    </div>
  );
}

export function RpDeviceToggle({
  value,
  onChange,
}: {
  value: RpDevice;
  onChange: (d: RpDevice) => void;
}) {
  return (
    <SegmentedControl
      size="sm"
      value={value}
      onChange={onChange}
      ariaLabel="Preview width"
      options={[
        { value: 'desktop', label: 'Desktop' },
        { value: 'tablet', label: 'Tablet' },
        { value: 'mobile', label: 'Phone' },
      ]}
    />
  );
}

/** Icons kept beside the toggle for callers that want a compact form. */
export const DEVICE_ICONS = { desktop: Monitor, tablet: Tablet, mobile: Smartphone };
